// src/components/button/index.tsx
import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { button } from "@pinatagrams/design-system/recipes";
import { motion } from "motion/react";
import { useCallback } from "react";

// src/components/button/motion.ts
import { DURATION, OPACITY } from "@pinatagrams/design-system";
var variants = {
  bold: {
    disabled: {
      opacity: OPACITY.disabled
    },
    rest: {
      y: "0"
    },
    rise: {
      y: "-.25rem"
    },
    active: {
      y: "-.25rem",
      transition: {
        duration: DURATION.rapid
      }
    },
    focus: {
      y: "-.25rem",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  subtle: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  DEFAULT: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  ghost: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  loud: {
    disabled: {
      opacity: OPACITY.disabled
    },
    rest: {
      scaleX: "100%",
      scaleY: "100%"
    },
    rise: {
      scaleX: "110%",
      scaleY: "90%"
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  }
};

// src/components/button/sound/click.mp3
var click_default = "data:audio/mpeg;base64,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";

// src/components/button/sound/release.mp3
var release_default = "data:audio/mpeg;base64,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";

// src/components/button/index.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var MotionSlot = motion(Slot);
var Button = React.forwardRef(
  ({ emphasis, children, size = "md", icons, disabled, asChild, className, style, ...props }, ref) => {
    const { onBlur, onFocus, onMouseDown, onMouseUp } = props;
    const buttonClassName = button({ emphasis, size });
    const mergedClassName = className ? `${buttonClassName} ${className}` : buttonClassName;
    const buttonRef = React.useRef(null);
    const audioRef = React.useRef(null);
    const LeftIcon = icons?.[0];
    const RightIcon = icons?.[1];
    const setRefs = useCallback(
      (node) => {
        buttonRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );
    const audioButtonPress = useCallback((state) => {
      audioRef.current = new Audio(state === "click" ? click_default : release_default);
      const playPromise = audioRef.current.play();
      if (playPromise instanceof Promise) {
        playPromise.catch((error) => {
          console.error("Error playing audio:", error);
        });
      }
    }, []);
    const handleOnMouseDown = useCallback(
      (event) => {
        onMouseDown?.(event);
        audioButtonPress("click");
      },
      [onMouseDown, audioButtonPress]
    );
    const handleOnMouseUp = useCallback(
      (event) => {
        onMouseUp?.(event);
        audioButtonPress("release");
      },
      [onMouseUp, audioButtonPress]
    );
    const handleOnFocus = useCallback(
      (event) => {
        onFocus?.(event);
      },
      [onFocus]
    );
    const handleOnBlur = useCallback(
      (event) => {
        onBlur?.(event);
      },
      [onBlur]
    );
    const Component = asChild ? MotionSlot : motion.button;
    const content = asChild ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
      LeftIcon && /* @__PURE__ */ jsx(LeftIcon, {}),
      children,
      RightIcon && /* @__PURE__ */ jsx(RightIcon, {})
    ] });
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref: setRefs,
        className: mergedClassName,
        style,
        transition: {
          duration: 0.2,
          ease: "linear"
        },
        variants: variants[emphasis] || variants["DEFAULT"],
        initial: "rest",
        animate: disabled ? "disabled" : "rest",
        whileHover: "rise",
        whileTap: "active",
        whileFocus: "focus",
        layout: "position",
        onMouseDown: handleOnMouseDown,
        onMouseUp: handleOnMouseUp,
        onFocus: handleOnFocus,
        onBlur: handleOnBlur,
        disabled: asChild ? void 0 : disabled,
        ...props,
        children: content
      }
    );
  }
);
Button.displayName = "Button";
var button_default = Button;

// src/components/text/index.tsx
import React2 from "react";
import { textRecipe } from "@pinatagrams/design-system/recipes";
import { jsx as jsx2 } from "react/jsx-runtime";
var Title = React2.forwardRef(
  ({ hierarchy = "title", emphasis = "strong", className, children, ...props }, ref) => {
    const slots = textRecipe({ hierarchy, titleEmphasis: emphasis });
    const cls = slots.title;
    const Tag = hierarchy === "splash" ? "h1" : hierarchy === "subtitle" ? "h3" : "h2";
    return (
      // @ts-expect-error dynamic heading tag
      /* @__PURE__ */ jsx2(Tag, { ref, className: [cls, className].filter(Boolean).join(" "), ...props, children })
    );
  }
);
Title.displayName = "Title";
var Body = React2.forwardRef(
  ({ emphasis = "DEFAULT", className, children, ...props }, ref) => {
    const slots = textRecipe({ bodyEmphasis: emphasis });
    const cls = slots.body;
    return /* @__PURE__ */ jsx2("p", { ref, className: [cls, className].filter(Boolean).join(" "), ...props, children });
  }
);
Body.displayName = "Body";
var Supplemental = React2.forwardRef(
  ({ emphasis = "DEFAULT", className, children, ...props }, ref) => {
    const slots = textRecipe({ supplementalEmphasis: emphasis });
    const cls = slots.supplemental;
    return /* @__PURE__ */ jsx2(
      "code",
      {
        ref,
        className: [cls, className].filter(Boolean).join(" "),
        ...props,
        children
      }
    );
  }
);
Supplemental.displayName = "Supplemental";
var text_default = Title;

// src/components/badge/index.tsx
import React3 from "react";
import { badge } from "@pinatagrams/design-system/recipes";
import { jsx as jsx3 } from "react/jsx-runtime";
var Badge = React3.forwardRef(
  ({ color, size = "md", children, ...props }, ref) => {
    const className = badge({ color, size });
    return /* @__PURE__ */ jsx3("span", { ref, role: "status", "aria-live": "polite", className, ...props, children });
  }
);
Badge.displayName = "Badge";
var badge_default = Badge;

// src/components/banner/index.tsx
import React7 from "react";
import { motion as motion4, AnimatePresence } from "motion/react";
import { banner } from "@pinatagrams/design-system/recipes";

// src/components/tooltip/index.tsx
import React5 from "react";
import { Tooltip as TT } from "radix-ui";
import { tooltip } from "@pinatagrams/design-system/recipes";
import { motion as motion2 } from "motion/react";

// src/contexts/layer-context.tsx
import React4 from "react";

// src/contexts/layer-types.ts
var LayerType = /* @__PURE__ */ ((LayerType2) => {
  LayerType2[LayerType2["Main"] = 0] = "Main";
  LayerType2[LayerType2["Nav"] = 1] = "Nav";
  LayerType2[LayerType2["Popover"] = 2] = "Popover";
  LayerType2[LayerType2["Toasts"] = 3] = "Toasts";
  LayerType2[LayerType2["Overlay"] = 4] = "Overlay";
  return LayerType2;
})(LayerType || {});
var layerNameMap = {
  main: 0 /* Main */,
  nav: 1 /* Nav */,
  popover: 2 /* Popover */,
  toasts: 3 /* Toasts */,
  overlay: 4 /* Overlay */
};

// src/contexts/layer-context.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var LayerContext = React4.createContext(null);
function Wrapper({
  children,
  layer
}) {
  const context = React4.useContext(LayerContext);
  if (!context) {
    throw new Error("[Layer] Layer.Wrapper must be used within Layer");
  }
  const resolvedLayer = typeof layer === "string" ? layerNameMap[layer] : layer;
  const nextLayer = resolvedLayer >= context.layer ? resolvedLayer : context.layer;
  return /* @__PURE__ */ jsx4(LayerContext.Provider, { value: { ...context, layer: nextLayer }, children });
}
function LayerRoot({ children }) {
  const baseLayerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  };
  return /* @__PURE__ */ jsxs2(LayerContext.Provider, { value: { layer: 0 /* Main */ }, children: [
    /* @__PURE__ */ jsx4("style", { children: `
				[data-layer] > * {
					pointer-events: auto;
				}
			` }),
    /* @__PURE__ */ jsx4("section", { id: "layer-popover", style: { ...baseLayerStyle, zIndex: 2 }, "data-layer": "popover" }),
    /* @__PURE__ */ jsx4("section", { id: "layer-toasts", style: { ...baseLayerStyle, zIndex: 3 }, "data-layer": "toasts" }),
    /* @__PURE__ */ jsx4(
      "section",
      {
        id: "layer-overlay",
        style: {
          ...baseLayerStyle,
          zIndex: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        },
        "data-layer": "overlay"
      }
    ),
    children
  ] });
}
function useLayerContainer(overrideLayer) {
  const context = React4.useContext(LayerContext);
  const [container, setContainer] = React4.useState(void 0);
  React4.useLayoutEffect(() => {
    const defaultContainer = document.getElementById("layer-popover");
    if (!defaultContainer) {
      throw new Error("[Layer] Missing default popover layer container");
    }
    if (!context) return setContainer(defaultContainer);
    let targetLayer = context.layer;
    if (overrideLayer !== void 0) {
      targetLayer = overrideLayer >= context.layer ? overrideLayer : context.layer;
    }
    if (targetLayer === 0 /* Main */ || targetLayer === 1 /* Nav */) {
      setContainer(defaultContainer);
      return;
    }
    const layerId = LayerType[targetLayer].toLowerCase();
    const element = document.getElementById(`layer-${layerId}`);
    if (!element) {
      throw new Error(`[Layer] Missing container for layer: ${layerId}`);
    }
    setContainer(element);
  }, [context, overrideLayer]);
  return container;
}
var Layer = Object.assign(LayerRoot, { Wrapper });

// src/components/tooltip/index.tsx
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var TooltipProvider = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx5(TT.Provider, { ...props, children });
};
var TooltipRoot = ({
  children,
  open,
  onOpenChange,
  delayDuration = 0
}) => {
  const [isOpen, setIsOpen] = React5.useState(open ?? false);
  React5.useEffect(() => {
    if (open !== void 0) {
      setIsOpen(open);
    }
  }, [open]);
  const handleOpenChange = (newOpen) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  return /* @__PURE__ */ jsx5(TT.Root, { delayDuration, open: isOpen, onOpenChange: handleOpenChange, children });
};
var TooltipTrigger = ({ children }) => {
  const triggerElement = React5.isValidElement(children) ? children : /* @__PURE__ */ jsx5("span", { children });
  return /* @__PURE__ */ jsx5(TT.Trigger, { asChild: true, children: triggerElement });
};
var TooltipContent = ({
  children,
  side = "top",
  sideOffset = 10,
  cardClassName,
  ...props
}) => {
  const container = useLayerContainer(2 /* Popover */);
  const MotionContent = React5.forwardRef((motionProps, ref) => /* @__PURE__ */ jsx5(motion2.div, { ref, ...motionProps }));
  MotionContent.displayName = "MotionContent";
  return /* @__PURE__ */ jsx5(TT.Portal, { container, children: /* @__PURE__ */ jsx5(TT.Content, { side, sideOffset, ...props, children: /* @__PURE__ */ jsxs3(
    MotionContent,
    {
      className: tooltip().root,
      initial: { opacity: 0, y: 20, scale: 0.8 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: {
        opacity: 0,
        y: 20,
        scale: 0.8,
        transition: { duration: 0.1 }
      },
      children: [
        /* @__PURE__ */ jsx5("div", { className: `${tooltip().card}${cardClassName ? ` ${cardClassName}` : ""}`, children }),
        /* @__PURE__ */ jsx5(TT.Arrow, { className: "tooltip-arrow" })
      ]
    }
  ) }) });
};
var Tooltip = Object.assign(TooltipRoot, {
  Trigger: TooltipTrigger,
  Content: TooltipContent,
  Provider: TooltipProvider
});
var tooltip_default = Tooltip;

// src/components/banner/index.tsx
import {
  CheckCircleIcon,
  WarningIcon,
  SmileyXEyesIcon,
  InfoIcon,
  XCircleIcon
} from "@phosphor-icons/react";

// src/components/layout/index.tsx
import React6 from "react";
import { motion as motion3 } from "motion/react";
import {
  stackLayout,
  rowLayout,
  pageSection,
  pageHeader,
  actionHeader,
  gridLayout,
  mainLayout,
  backdrop,
  scrollContainer
} from "@pinatagrams/design-system/recipes";
import { DURATION as DURATION2, EASING } from "@pinatagrams/design-system";
import { jsx as jsx6 } from "react/jsx-runtime";
var Stack = React6.forwardRef(
  ({ gap = "md", align = "stretch", skeleton = false, className, ...props }, ref) => {
    const { root } = stackLayout({ gap, align, skeleton });
    return /* @__PURE__ */ jsx6("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Stack.displayName = "Stack";
var Inline = React6.forwardRef(
  ({ gap = "md", align = "center", justify = "start", wrap = true, className, ...props }, ref) => {
    const { root } = rowLayout({ gap, align, justify, wrap });
    return /* @__PURE__ */ jsx6("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Inline.displayName = "Inline";
var PageSection = React6.forwardRef(
  ({ maxWidth = "lg", className, ...props }, ref) => {
    const { root } = pageSection({ maxWidth });
    return /* @__PURE__ */ jsx6("section", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
PageSection.displayName = "PageSection";
var PageHeader = React6.forwardRef(
  ({ className, ...props }, ref) => {
    const { root } = pageHeader({});
    return /* @__PURE__ */ jsx6("header", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
PageHeader.displayName = "PageHeader";
var HeaderRoot = React6.forwardRef(
  ({ sticky = false, skeleton = false, className, ...props }, ref) => {
    const { root } = actionHeader({ sticky, skeleton });
    return /* @__PURE__ */ jsx6("header", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
HeaderRoot.displayName = "Header";
var HeaderTitleSection = React6.forwardRef(({ className, ...props }, ref) => {
  const { title } = actionHeader({});
  return /* @__PURE__ */ jsx6("div", { ref, className: `${title}${className ? ` ${className}` : ""}`, ...props });
});
HeaderTitleSection.displayName = "Header.Title";
var HeaderActionsSection = React6.forwardRef(({ className, ...props }, ref) => {
  const { actions } = actionHeader({});
  return /* @__PURE__ */ jsx6("div", { ref, className: `${actions}${className ? ` ${className}` : ""}`, ...props });
});
HeaderActionsSection.displayName = "Header.Actions";
var Header = Object.assign(HeaderRoot, {
  Title: HeaderTitleSection,
  Actions: HeaderActionsSection
});
var ActionHeader = Header;
var Main = React6.forwardRef(
  ({ gap = "lg", padding = "lg", skeleton = false, className, ...props }, ref) => {
    const { root } = mainLayout({ gap, padding, skeleton });
    return /* @__PURE__ */ jsx6("main", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Main.displayName = "Main";
var Grid = React6.forwardRef(
  ({
    gap = "md",
    justify = "center",
    align = "start",
    items = "center",
    overflow = "auto",
    flex = "fill",
    padding = "lg",
    className,
    ...props
  }, ref) => {
    const { root } = gridLayout({ gap, justify, align, items, overflow, flex, padding });
    return /* @__PURE__ */ jsx6("section", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Grid.displayName = "Grid";
var Overlay = React6.forwardRef(
  ({ status, origin, pos, elevation, size, children, exited }, ref) => {
    const coords = origin || pos;
    const maskType = origin ? "origin" : pos !== void 0 ? "pos" : void 0;
    const { root } = backdrop({ status, elevation, size });
    return /* @__PURE__ */ jsx6(
      motion3.div,
      {
        ref,
        className: root,
        variants: getBackdropVariants(coords, maskType, exited),
        custom: coords,
        initial: "closed",
        animate: "open",
        exit: "closed",
        children
      }
    );
  }
);
Overlay.displayName = "Overlay";
var getBackdropVariants = (coords, maskType, exited) => {
  const transitions = {
    backgroundSize: {
      duration: DURATION2.slow,
      ease: EASING.out
    },
    backdropFilter: {
      ease: EASING.out,
      duration: DURATION2.normal
    },
    opacity: {
      duration: DURATION2.normal,
      ease: EASING.inOut
    },
    maskImage: {
      duration: DURATION2.fast,
      ease: EASING.out
    }
  };
  const baseVariants = {
    open: {
      backgroundSize: "4px 4px",
      backdropFilter: "brightness(0.7) blur(3px)",
      opacity: 1
    },
    closed: {
      backgroundSize: "3px 3px",
      backdropFilter: "brightness(1) blur(5px)",
      opacity: 0
    }
  };
  if (coords === void 0 || maskType === void 0) {
    return {
      open: { ...baseVariants.open },
      closed: { ...baseVariants.closed }
    };
  }
  if (maskType === "origin") {
    return {
      open: ({ x, y }) => ({
        ...baseVariants.open,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 10%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
        transition: {
          ...transitions,
          maskImage: {
            duration: DURATION2.slow,
            ease: EASING.in
          }
        }
      }),
      closed: ({ x, y }) => ({
        ...baseVariants.closed,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%, rgba(0,0,0,1) 100%, rgba(0,0,0,1) 100%)`,
        transition: {
          maskImage: {
            duration: DURATION2.slow,
            ease: EASING.out
          }
        }
      })
    };
  }
  return {
    open: ({ x, y }) => {
      if (exited) {
        return {
          ...baseVariants.open,
          maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
          transition: {
            ...transitions,
            maskImage: {
              duration: DURATION2.slow,
              ease: EASING.in
            }
          }
        };
      }
      return {
        ...baseVariants.open,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
        transition: {
          ...transitions,
          maskImage: {
            duration: DURATION2.instant
          }
        }
      };
    },
    closed: ({ x, y }) => {
      if (exited) {
        return {
          ...baseVariants.closed,
          maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
          transition: {
            maskImage: {
              duration: DURATION2.slow,
              ease: EASING.out
            }
          }
        };
      }
      return {
        ...baseVariants.closed,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%, rgba(0,0,0,1) 100%, rgba(0,0,0,1) 100%)`,
        transition: {
          maskImage: {
            duration: DURATION2.slow,
            ease: EASING.out
          }
        }
      };
    }
  };
};
var ScrollContainer = React6.forwardRef(({ className, ...props }, ref) => {
  const { root } = scrollContainer({ size: "element" });
  return /* @__PURE__ */ jsx6("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
});
ScrollContainer.displayName = "ScrollContainer";

// src/components/banner/index.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var getBannerStatus = (value) => {
  if (!value) return "info";
  if (value.success === true) return "success";
  if (value.severity === "fatal") return "fatal";
  if (value.severity === "nonfatal") return "error";
  if (value.intent === "object") return "warning";
  if (value.intent === "confirm") return "info";
  return "info";
};
var Banner = ({ value }) => {
  const status = getBannerStatus(value);
  const { root: classes, action: actionClasses } = banner({ status });
  const Badge2 = (() => {
    switch (status) {
      case "success":
        return CheckCircleIcon;
      case "warning":
        return WarningIcon;
      case "fatal":
        return SmileyXEyesIcon;
      case "error":
        return XCircleIcon;
      case "info":
        return InfoIcon;
      default:
        return InfoIcon;
    }
  })();
  return /* @__PURE__ */ jsx7(AnimatePresence, { mode: "popLayout", children: value && /* @__PURE__ */ jsxs4(
    motion4.article,
    {
      className: classes,
      role: "region",
      "aria-live": "polite",
      variants: variants2[status === "success" ? "ok" : "error"],
      initial: "hidden",
      animate: "visible",
      exit: "hidden",
      transition: {
        clipPath: { duration: 0.6, ease: "easeInOut" },
        opacity: { duration: 0.3 }
      },
      children: [
        /* @__PURE__ */ jsxs4(Stack, { children: [
          /* @__PURE__ */ jsxs4(Inline, { justify: "between", children: [
            value.title ? /* @__PURE__ */ jsx7("strong", { children: value.title }) : null,
            /* @__PURE__ */ jsx7(Badge2, { "data-icon-status": true, weight: "bold" })
          ] }),
          value.message ? /* @__PURE__ */ jsx7(Body, { children: value.message }) : null,
          typeof value.progress === "number" ? /* @__PURE__ */ jsx7("div", { style: { marginTop: "8px" }, children: /* @__PURE__ */ jsx7(
            "div",
            {
              style: {
                width: "100%",
                height: "4px",
                backgroundColor: "rgba(0,0,0,0.1)",
                borderRadius: "2px",
                overflow: "hidden"
              },
              role: "progressbar",
              "aria-valuenow": value.progress,
              "aria-valuemin": 0,
              "aria-valuemax": 100,
              "aria-label": `Progress: ${value.progress}%`,
              children: /* @__PURE__ */ jsx7(
                motion4.div,
                {
                  initial: { width: 0 },
                  animate: { width: `${value.progress}%` },
                  transition: { duration: 0.3 },
                  style: {
                    height: "100%",
                    backgroundColor: "currentColor",
                    opacity: 0.6
                  }
                }
              )
            }
          ) }) : null
        ] }),
        value.actions?.length ? /* @__PURE__ */ jsx7("div", { className: actionClasses, children: value.actions.map((action, index) => {
          const button2 = /* @__PURE__ */ jsx7(button_default, { emphasis: "subtle", onClick: action.onClick, disabled: action.disabled, children: action.label });
          return action.tooltip ? /* @__PURE__ */ jsxs4(tooltip_default, { children: [
            /* @__PURE__ */ jsx7(tooltip_default.Trigger, { children: button2 }),
            /* @__PURE__ */ jsx7(tooltip_default.Content, { children: action.tooltip })
          ] }, index) : /* @__PURE__ */ jsx7(React7.Fragment, { children: button2 }, index);
        }) }) : null
      ]
    },
    value.id ?? value.origin.id
  ) });
};
Banner.displayName = "Banner";
var banner_default = Banner;
var variants2 = {
  ok: {
    hidden: {
      clipPath: "circle(0% at 0% 0%)",
      opacity: 0
    },
    visible: {
      clipPath: "circle(150% at 0% 0%)",
      opacity: 1
    }
  },
  error: {
    hidden: {
      x: 0,
      opacity: 0
    },
    visible: {
      x: [-10, 10, -10, 10, -5, 5, 0],
      opacity: 1
    }
  }
};

// src/components/card/index.tsx
import React8 from "react";
import { motion as motion5 } from "motion/react";
import { DURATION as DURATION3, EASING as EASING2, OPACITY as OPACITY2, SPRING } from "@pinatagrams/design-system";
import { card } from "@pinatagrams/design-system/recipes";
import { jsx as jsx8 } from "react/jsx-runtime";
var MOTION_CARD_COMPONENTS = {
  div: motion5.div,
  article: motion5.article,
  section: motion5.section,
  aside: motion5.aside
};
var MOTION_ANCHOR = motion5.a;
var Card = React8.forwardRef(
  ({
    interactive = false,
    elevation = "flat",
    variant = "default",
    as = "div",
    children,
    className,
    url
  }, ref) => {
    const classes = card({ interactive, elevation, variant }) + (className ? ` ${className}` : "");
    const Component = MOTION_CARD_COMPONENTS[as];
    const cardContent = /* @__PURE__ */ jsx8(
      Component,
      {
        ref,
        className: classes,
        initial: interactive ? "hidden" : false,
        animate: "show",
        whileHover: interactive ? "hover" : void 0,
        variants: {
          hidden: { opacity: OPACITY2.hidden, y: 8 },
          show: {
            opacity: OPACITY2.visible,
            y: 0,
            transition: { duration: DURATION3.slow, ease: EASING2.out }
          },
          hover: {
            y: -4,
            boxShadow: "var(--shadow-raised)",
            transition: SPRING.smooth
          }
        },
        children
      }
    );
    if (url) {
      return /* @__PURE__ */ jsx8(MOTION_ANCHOR, { href: url, children: cardContent });
    }
    return cardContent;
  }
);
Card.displayName = "Card";
var card_default = Card;

// src/components/label/index.tsx
import React9 from "react";
import { label } from "@pinatagrams/design-system/recipes";
import { jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var LabelRoot = React9.forwardRef(
  ({ width = 8, height = 4, unit = "in", className, style, children, ...props }, ref) => {
    const customStyle = {
      "--label-width": String(width),
      "--label-height": String(height),
      "--label-unit": `1${unit}`,
      ...style
    };
    return /* @__PURE__ */ jsx9(
      "div",
      {
        ref,
        className: `${label({}).root}${className ? ` ${className}` : ""}`,
        style: customStyle,
        ...props,
        children
      }
    );
  }
);
LabelRoot.displayName = "Label";
var LabelGroup = React9.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx9("div", { ref, className: `${label({}).group}${className ? ` ${className}` : ""}`, ...props, children });
  }
);
LabelGroup.displayName = "Label.Group";
var LabelDetails = React9.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx9(
      "div",
      {
        ref,
        className: `${label({}).details}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelDetails.displayName = "Label.Details";
var LabelSection = React9.forwardRef(
  ({ variant = "default", className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx9(
      "section",
      {
        ref,
        className: `${label({ sectionVariant: variant }).section}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelSection.displayName = "Label.Section";
var LabelSku = React9.forwardRef(
  ({ emoji, className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsxs5("h2", { ref, className: `${label({}).sku}${className ? ` ${className}` : ""}`, ...props, children: [
      emoji && /* @__PURE__ */ jsx9("span", { className: label({}).emoji, children: emoji }),
      /* @__PURE__ */ jsx9("span", { children }),
      emoji && /* @__PURE__ */ jsx9("span", { className: label({}).emoji, children: emoji })
    ] });
  }
);
LabelSku.displayName = "Label.Sku";
var LabelPill = React9.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx9("div", { ref, className: `${label({}).pill}${className ? ` ${className}` : ""}`, ...props, children });
  }
);
LabelPill.displayName = "Label.Pill";
var LabelBulk = React9.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx9("div", { ref, className: `${label({}).bulk}${className ? ` ${className}` : ""}`, ...props, children });
  }
);
LabelBulk.displayName = "Label.Bulk";
var LabelBulkBox = React9.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx9(
    "span",
    {
      ref,
      className: `${label({}).bulkBox}${className ? ` ${className}` : ""}`,
      ...props,
      children
    }
  );
});
LabelBulkBox.displayName = "Label.BulkBox";
var LabelDivider = React9.forwardRef(
  ({ highlight = false, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx9(
      "div",
      {
        ref,
        className: `${label({ dividerHighlight: highlight }).divider}${className ? ` ${className}` : ""}`,
        "data-highlight": highlight,
        ...props
      }
    );
  }
);
LabelDivider.displayName = "Label.Divider";
var LabelName = React9.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx9("h5", { ref, className: `${label({}).name}${className ? ` ${className}` : ""}`, ...props, children });
});
LabelName.displayName = "Label.Name";
var LabelAddress = React9.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx9("p", { ref, className: `${label({}).address}${className ? ` ${className}` : ""}`, ...props, children });
});
LabelAddress.displayName = "Label.Address";
var LabelMessage = React9.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx9(
      "pre",
      {
        ref,
        className: `${label({}).message}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelMessage.displayName = "Label.Message";
var LabelId = React9.forwardRef(
  ({ className, children, ...props }, ref) => {
    const dataExternal = props["data-external"] === "true";
    const dataOverdue = props["data-overdue"] === "true";
    return /* @__PURE__ */ jsx9(
      "pre",
      {
        ref,
        className: `${label({ idExternal: dataExternal, idOverdue: dataOverdue }).id}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelId.displayName = "Label.Id";
var LabelGraphic = React9.forwardRef(
  ({ className, alt = "Label graphic", ...props }, ref) => {
    return /* @__PURE__ */ jsx9(
      "img",
      {
        ref,
        className: `${label({}).graphic}${className ? ` ${className}` : ""}`,
        alt,
        loading: "eager",
        ...props
      }
    );
  }
);
LabelGraphic.displayName = "Label.Graphic";
var LabelMessageOverlay = React9.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx9(
    "div",
    {
      ref,
      className: `${label({}).messageOverlay}${className ? ` ${className}` : ""}`,
      ...props,
      children
    }
  );
});
LabelMessageOverlay.displayName = "Label.MessageOverlay";
var LabelMessageText = React9.forwardRef(({ className, children, style, onChange, onBlur, onFocus, onInput, ...props }, ref) => {
  const messageTextClass = `${label({}).messageText}${className ? ` ${className}` : ""}`;
  const hasCallbacks = Boolean(onChange || onBlur || onFocus || onInput);
  const textareaProps = props;
  if (hasCallbacks) {
    return /* @__PURE__ */ jsx9(
      "textarea",
      {
        ref,
        className: messageTextClass,
        style: {
          ...style
        },
        onChange,
        onBlur,
        onFocus,
        onInput,
        ...textareaProps
      }
    );
  }
  return /* @__PURE__ */ jsx9(
    "div",
    {
      ref,
      className: messageTextClass,
      style,
      ...props,
      children
    }
  );
});
LabelMessageText.displayName = "Label.MessageText";
var Label = Object.assign(LabelRoot, {
  Group: LabelGroup,
  Details: LabelDetails,
  Section: LabelSection,
  Sku: LabelSku,
  Pill: LabelPill,
  Bulk: LabelBulk,
  BulkBox: LabelBulkBox,
  Divider: LabelDivider,
  Name: LabelName,
  Address: LabelAddress,
  Message: LabelMessage,
  Id: LabelId,
  Graphic: LabelGraphic,
  MessageOverlay: LabelMessageOverlay,
  MessageText: LabelMessageText
});
var label_default = Label;

// src/components/input/index.tsx
import React10 from "react";
import { input } from "@pinatagrams/design-system/recipes";
import { motion as motion6 } from "motion/react";
import { jsx as jsx10 } from "react/jsx-runtime";
var TextInput = React10.forwardRef(({ size, emphasis, ...props }, ref) => {
  const className = input({ size, emphasis });
  return /* @__PURE__ */ jsx10(motion6.input, { ref, className, ...props });
});
TextInput.displayName = "TextInput";
var input_default = TextInput;

// src/components/toast/index.tsx
import React13 from "react";
import { motion as motion8, AnimatePresence as AnimatePresence2 } from "motion/react";
import { Toast as Toast3, Portal } from "radix-ui";
import { toastRecipe as toastRecipe2 } from "@pinatagrams/design-system/recipes";

// src/components/toast/ToastItem.tsx
import React11 from "react";
import { motion as motion7 } from "motion/react";
import { Toast } from "radix-ui";
import {
  InfoIcon as InfoIcon2,
  CheckCircleIcon as CheckCircleIcon2,
  WarningIcon as WarningIcon2,
  SmileyXEyesIcon as SmileyXEyesIcon2,
  CaretRightIcon,
  XCircleIcon as XCircleIcon2
} from "@phosphor-icons/react";
import { SPRING as SPRING3 } from "@pinatagrams/design-system";
import { toastRecipe } from "@pinatagrams/design-system/recipes";

// src/components/toast/motion.ts
import { DELAY, DURATION as DURATION4, OPACITY as OPACITY3, SCALE, SPRING as SPRING2 } from "@pinatagrams/design-system";
var toastStackVariants = {
  open: (index) => ({
    y: "0%",
    scale: SCALE.default,
    opacity: OPACITY3.visible,
    pointerEvents: "auto",
    cursor: "pointer",
    transition: {
      delay: DELAY.stagger(index)
    }
  }),
  closed: (index) => ({
    y: `${index * 100}%`,
    scale: SCALE.default - index * 0.08,
    opacity: OPACITY3.visible - index * 0.4,
    pointerEvents: index === 0 ? "auto" : "none",
    cursor: index === 0 ? "pointer" : "default",
    "--index": index,
    transition: {
      delay: DELAY.stagger(index)
    }
  }),
  exit: {
    opacity: OPACITY3.hidden,
    scale: SCALE.microDown,
    y: 20,
    transition: { duration: DURATION4.normal }
  },
  hover: {}
};
var caretVariants = {
  open: {
    x: 0,
    opacity: 1
  },
  closed: {
    x: 0,
    opacity: 0
  },
  hover: {
    x: 4
  }
};
var stackTransition = {
  ...SPRING2.crisp
};

// src/components/toast/ToastItem.tsx
import { jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
var ToastItem = React11.forwardRef(
  ({
    status,
    path,
    duration,
    index,
    reverseIndex,
    onOpenChange,
    open,
    title,
    description,
    progress
  }, ref) => {
    const { collapsed, setCollapsed } = React11.useContext(ToastStackContext);
    const classes = toastRecipe({
      status,
      size: "md",
      state: collapsed ? "collapsed" : "expanded"
    });
    const Icon = (() => {
      switch (status) {
        case "info":
          return InfoIcon2;
        case "success":
          return CheckCircleIcon2;
        case "warning":
          return WarningIcon2;
        case "fatal":
          return SmileyXEyesIcon2;
        case "error":
          return XCircleIcon2;
        default:
          return InfoIcon2;
      }
    })();
    const goToPath = () => {
      window.location.href = path.url;
    };
    const CaretIcon = motion7(CaretRightIcon);
    return /* @__PURE__ */ jsx11(
      Toast.Root,
      {
        open,
        onOpenChange,
        duration: duration ?? 0,
        asChild: true,
        forceMount: true,
        children: /* @__PURE__ */ jsxs6(
          motion7.section,
          {
            role: "status",
            "aria-live": "polite",
            className: classes.root,
            variants: toastStackVariants,
            custom: index,
            animate: collapsed ? "closed" : "open",
            exit: "exit",
            initial: false,
            whileHover: collapsed ? void 0 : "hover",
            onClick: collapsed ? setCollapsed.bind(null, false) : goToPath,
            "aria-label": collapsed ? `Go to ${path.label}` : void 0,
            style: {
              zIndex: reverseIndex
            },
            ref,
            children: [
              /* @__PURE__ */ jsx11(
                motion7.button,
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    onOpenChange(false);
                  },
                  style: {
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center"
                  },
                  "aria-label": "Dismiss notification",
                  children: /* @__PURE__ */ jsx11(Icon, { "data-icon-type": "status", weight: "bold" })
                }
              ),
              /* @__PURE__ */ jsxs6(Stack, { gap: "none", children: [
                title ? /* @__PURE__ */ jsx11(Toast.Title, { asChild: true, children: /* @__PURE__ */ jsx11("strong", { children: title }) }) : null,
                description ? /* @__PURE__ */ jsx11(Toast.Description, { asChild: true, "data-part": "description", children: /* @__PURE__ */ jsx11("span", { children: description }) }) : null,
                " ",
                typeof progress === "number" ? /* @__PURE__ */ jsx11("div", { style: { marginTop: "8px" }, children: /* @__PURE__ */ jsx11(
                  "div",
                  {
                    style: {
                      width: "100%",
                      height: "4px",
                      backgroundColor: "rgba(0,0,0,0.1)",
                      borderRadius: "2px",
                      overflow: "hidden"
                    },
                    role: "progressbar",
                    "aria-valuenow": progress,
                    "aria-valuemin": 0,
                    "aria-valuemax": 100,
                    "aria-label": `Progress: ${progress}%`,
                    children: /* @__PURE__ */ jsx11(
                      motion7.div,
                      {
                        initial: { width: 0 },
                        animate: { width: `${progress}%` },
                        transition: { duration: 0.3 },
                        style: {
                          height: "100%",
                          backgroundColor: "currentColor",
                          opacity: 0.6
                        }
                      }
                    )
                  }
                ) }) : null,
                " "
              ] }),
              /* @__PURE__ */ jsx11(
                CaretIcon,
                {
                  "data-icon-type": "caret",
                  weight: "bold",
                  variants: caretVariants,
                  transition: SPRING3.responsive
                }
              )
            ]
          }
        )
      }
    );
  }
);
ToastItem.displayName = "ToastItem";
var ToastItem_default = ToastItem;

// src/components/toast/toast-stack-context.tsx
import React12 from "react";
var ToastStackContext = React12.createContext({
  collapsed: false,
  setCollapsed: () => {
  },
  expandedToastId: void 0,
  setExpandedToastId: () => {
  },
  toastCount: 0,
  setToastCount: () => {
  }
});

// src/components/toast/toast-provider.tsx
import { Toast as Toast2 } from "radix-ui";
var ToastProvider = Toast2.Provider;

// src/components/toast/index.tsx
import { jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
var Toasts = React13.forwardRef((props, forwardedRef) => {
  void props;
  const [toasts, setToasts] = React13.useState(
    []
  );
  const { setCollapsed, setToastCount } = React13.useContext(ToastStackContext);
  React13.useEffect(() => {
    const shouldExpand = toasts.length === 1;
    setCollapsed(!shouldExpand);
    setToastCount(toasts.length);
  }, [toasts.length, setCollapsed, setToastCount]);
  React13.useImperativeHandle(forwardedRef, () => ({
    publish: (toastData) => {
      const id = `toast-${Date.now()}`;
      setToasts((prev) => {
        const toast = { ...toastData, id, open: true };
        const next = [toast, ...prev];
        return next;
      });
      return id;
    },
    dismiss: (id) => {
      setToasts((prev) => prev.map((t) => t.id === id ? { ...t, open: false } : t));
    }
  }));
  const handleOpenChange = (id, open) => {
    if (!open) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 200);
    }
    setToasts((prev) => prev.map((t) => t.id === id ? { ...t, open } : t));
  };
  return /* @__PURE__ */ jsx12(AnimatePresence2, { children: toasts.map((toastInstance, index) => /* @__PURE__ */ jsx12(
    ToastItem_default,
    {
      ...toastInstance,
      index,
      reverseIndex: toasts.length - index,
      onOpenChange: (open) => handleOpenChange(toastInstance.id, open)
    },
    toastInstance.id
  )) });
});
var Viewport = (props) => {
  const { collapsed } = React13.useContext(ToastStackContext);
  const viewportRef = React13.useRef(null);
  const container = useLayerContainer();
  const stackVariants = {
    open: {
      scale: 0.9,
      cursor: "pointer",
      maxHeight: "none"
    },
    closed: {
      y: 0,
      scale: 1,
      cursor: "default",
      maxHeight: "3.5rem"
    }
  };
  return /* @__PURE__ */ jsx12(Portal.Root, { container, asChild: true, children: /* @__PURE__ */ jsxs7(motion8.section, { className: toastRecipe2().viewport, children: [
    /* @__PURE__ */ jsx12(StackHeader, {}),
    /* @__PURE__ */ jsx12(Toast3.Viewport, { asChild: true, ...props, children: /* @__PURE__ */ jsx12(
      motion8.ol,
      {
        ref: viewportRef,
        className: toastRecipe2().list,
        variants: stackVariants,
        initial: false,
        animate: collapsed ? "closed" : "open",
        style: { overflow: collapsed ? "hidden" : "visible" }
      }
    ) })
  ] }) });
};
Viewport.displayName = "ToastViewport";
var StackHeader = () => {
  const { setCollapsed, collapsed, toastCount } = React13.useContext(ToastStackContext);
  const variants3 = {
    open: { y: 0, scale: 1, opacity: 1 },
    closed: { y: 60, scale: 0.8, opacity: 0 }
  };
  if (toastCount < 2) return null;
  return /* @__PURE__ */ jsxs7(
    motion8.header,
    {
      className: toastRecipe2().header,
      variants: variants3,
      initial: "closed",
      animate: collapsed ? "closed" : "open",
      transition: {
        type: "spring",
        stiffness: 600,
        damping: 50,
        delay: collapsed ? 0.2 : 0
      },
      children: [
        /* @__PURE__ */ jsx12(Title, { children: "Notifications" }),
        /* @__PURE__ */ jsx12(button_default, { size: "sm", onClick: () => setCollapsed(true), children: "Collapse" })
      ]
    }
  );
};
StackHeader.displayName = "ToastStackHeader";
var ToastWithViewport = Object.assign(Toasts, { Viewport });
Toasts.displayName = "Toasts";
var toast_default = ToastWithViewport;

// src/components/alert-dialog/index.tsx
import { motion as motion9, AnimatePresence as AnimatePresence3 } from "motion/react";
import { AlertDialog as RadixAlertDialog } from "radix-ui";
import { DELAY as DELAY2, DURATION as DURATION5, EASING as EASING3 } from "@pinatagrams/design-system";
import { alertDialog } from "@pinatagrams/design-system/recipes";
import { jsx as jsx13, jsxs as jsxs8 } from "react/jsx-runtime";
var AlertDialogRoot = ({ open, onOpenChange, children }) => {
  return /* @__PURE__ */ jsx13(RadixAlertDialog.Root, { open, onOpenChange, children });
};
var AlertDialogTrigger = ({ children }) => {
  return /* @__PURE__ */ jsx13(RadixAlertDialog.Trigger, { asChild: true, children });
};
var AlertDialogContent = ({
  status = "info",
  showOverlay = true,
  children,
  ...props
}) => {
  const container = useLayerContainer();
  const recipeStatus = status === "info" ? "confirm" : status;
  const { root: contentClass } = alertDialog({
    success: recipeStatus
  });
  return /* @__PURE__ */ jsx13(AnimatePresence3, { children: /* @__PURE__ */ jsxs8(RadixAlertDialog.Portal, { container, children: [
    showOverlay && /* @__PURE__ */ jsx13(RadixAlertDialog.Overlay, { asChild: true, children: /* @__PURE__ */ jsx13(Overlay, { status }) }),
    /* @__PURE__ */ jsx13(RadixAlertDialog.Content, { className: contentClass, ...props, asChild: true, children: /* @__PURE__ */ jsx13(
      motion9.article,
      {
        role: "presentation",
        variants: contentVariants,
        initial: "closed",
        animate: "open",
        exit: "closed",
        children
      }
    ) })
  ] }) });
};
var AlertDialogTitle = ({ children }) => {
  return /* @__PURE__ */ jsx13(RadixAlertDialog.Title, { asChild: true, children: /* @__PURE__ */ jsx13(Title, { hierarchy: "subtitle", children }) });
};
var AlertDialogDescription = ({ children }) => {
  return /* @__PURE__ */ jsx13(RadixAlertDialog.Description, { asChild: true, children: /* @__PURE__ */ jsx13(Body, { children }) });
};
var AlertDialogActions = ({ children }) => {
  const { actions: actionsClass } = alertDialog({});
  return /* @__PURE__ */ jsx13("footer", { className: actionsClass, children });
};
var AlertDialogAction = ({
  emphasis = "bold",
  onClick,
  children
}) => {
  return /* @__PURE__ */ jsx13(RadixAlertDialog.Action, { asChild: true, children: /* @__PURE__ */ jsx13(button_default, { emphasis, onClick, children }) });
};
var AlertDialogCancel = ({
  emphasis = "ghost",
  onClick,
  children
}) => {
  return /* @__PURE__ */ jsx13(RadixAlertDialog.Cancel, { asChild: true, children: /* @__PURE__ */ jsx13(button_default, { emphasis, onClick, children }) });
};
var contentVariants = {
  open: {
    y: "0%",
    opacity: 1,
    transition: { duration: DURATION5.moderate, ease: EASING3.out, delay: DELAY2.long }
  },
  closed: {
    y: "30%",
    opacity: 0,
    transition: { duration: DURATION5.moderate, ease: EASING3.in }
  }
};
var AlertDialog = Object.assign(AlertDialogRoot, {
  Trigger: AlertDialogTrigger,
  Content: AlertDialogContent,
  Title: AlertDialogTitle,
  Description: AlertDialogDescription,
  Actions: AlertDialogActions,
  Action: AlertDialogAction,
  Cancel: AlertDialogCancel
});

// src/components/dialog/index.tsx
import { motion as motion10, AnimatePresence as AnimatePresence4 } from "motion/react";
import { Dialog as RadixDialog } from "radix-ui";
import { DELAY as DELAY3, DURATION as DURATION6, EASING as EASING4 } from "@pinatagrams/design-system";
import { dialog } from "@pinatagrams/design-system/recipes";
import { jsx as jsx14, jsxs as jsxs9 } from "react/jsx-runtime";
var DialogRoot = ({ open, onOpenChange, children }) => {
  return /* @__PURE__ */ jsx14(RadixDialog.Root, { open, onOpenChange, children });
};
var DialogTrigger = ({ children }) => {
  return /* @__PURE__ */ jsx14(RadixDialog.Trigger, { asChild: true, children });
};
var DialogContent = ({
  showOverlay = true,
  children,
  ...props
}) => {
  const container = useLayerContainer();
  const { root: contentClass, close: closeClass } = dialog({});
  return /* @__PURE__ */ jsx14(AnimatePresence4, { children: /* @__PURE__ */ jsxs9(RadixDialog.Portal, { container, children: [
    showOverlay && /* @__PURE__ */ jsx14(RadixDialog.Overlay, { asChild: true, children: /* @__PURE__ */ jsx14(Overlay, { status: "info" }) }),
    /* @__PURE__ */ jsx14(RadixDialog.Content, { className: contentClass, ...props, asChild: true, children: /* @__PURE__ */ jsxs9(
      motion10.article,
      {
        role: "presentation",
        variants: contentVariants2,
        initial: "closed",
        animate: "open",
        exit: "closed",
        children: [
          /* @__PURE__ */ jsx14(RadixDialog.Close, { asChild: true, children: /* @__PURE__ */ jsx14(button_default, { emphasis: "ghost", size: "sm", className: closeClass, "aria-label": "Close dialog", children: "Close" }) }),
          children
        ]
      }
    ) })
  ] }) });
};
var DialogTitle = ({ children }) => {
  const { title: titleClass } = dialog({});
  return /* @__PURE__ */ jsx14(RadixDialog.Title, { asChild: true, children: /* @__PURE__ */ jsx14(Title, { hierarchy: "subtitle", className: titleClass, children }) });
};
var DialogBody = ({ children }) => {
  const { body: bodyClass } = dialog({});
  return /* @__PURE__ */ jsx14("div", { className: bodyClass, children });
};
var DialogFooter = ({ children }) => {
  const { footer: footerClass } = dialog({});
  return /* @__PURE__ */ jsx14("footer", { className: footerClass, children });
};
var contentVariants2 = {
  open: {
    y: "0%",
    opacity: 1,
    transition: { duration: DURATION6.moderate, ease: EASING4.out, delay: DELAY3.long }
  },
  closed: {
    y: "30%",
    opacity: 0,
    transition: { duration: DURATION6.moderate, ease: EASING4.in }
  }
};
var Dialog = Object.assign(DialogRoot, {
  Trigger: DialogTrigger,
  Content: DialogContent,
  Title: DialogTitle,
  Body: DialogBody,
  Footer: DialogFooter
});

// src/components/dropdown-menu/index.tsx
import React14, { useCallback as useCallback2 } from "react";
import { DropdownMenu as DM } from "radix-ui";
import { AnimatePresence as AnimatePresence5, motion as motion11, useMotionValue, useTransform } from "motion/react";
import { DURATION as DURATION7, EASING as EASING5, SPRING as SPRING4 } from "@pinatagrams/design-system";
import { dropdownMenuContent } from "@pinatagrams/design-system/recipes";
import { Fragment as Fragment2, jsx as jsx15, jsxs as jsxs10 } from "react/jsx-runtime";
var DropdownMenu = ({
  trigger,
  children,
  open,
  onOpenChange,
  overlay = false,
  ...contentProps
}) => {
  const [isOpen, setIsOpen] = React14.useState(open ?? false);
  const targetLayer = overlay ? 4 /* Overlay */ : 2 /* Popover */;
  const container = useLayerContainer(targetLayer);
  const menuRef = React14.useRef(null);
  const [pos, setPos] = React14.useState(void 0);
  React14.useEffect(() => {
    if (open !== void 0) {
      setIsOpen(open);
    }
  }, [open]);
  const handleOnPlaced = useCallback2(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setPos({ x: centerX, y: centerY });
    } else if (!isOpen) {
    }
  }, [menuRef, isOpen]);
  const handleOpenChange = (newOpen) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  return /* @__PURE__ */ jsxs10(DM.Root, { open: isOpen, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx15(DM.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx15(AnimatePresence5, { children: isOpen && /* @__PURE__ */ jsx15(DM.Portal, { container, forceMount: true, children: /* @__PURE__ */ jsxs10(Fragment2, { children: [
      overlay && pos && /* @__PURE__ */ jsx15(Overlay, { status: "info", origin: pos }, "overlay"),
      /* @__PURE__ */ jsx15(
        DM.Content,
        {
          sideOffset: 2,
          ...contentProps,
          forceMount: true,
          onPlaced: handleOnPlaced,
          children: /* @__PURE__ */ jsxs10(
            motion11.article,
            {
              className: dropdownMenuContent({}).content,
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              transition: { duration: 0.15 },
              ref: menuRef,
              children: [
                children,
                /* @__PURE__ */ jsx15(DM.Arrow, { className: dropdownMenuContent({}).arrow, width: "1em", height: ".5em" })
              ]
            }
          )
        }
      )
    ] }) }) })
  ] });
};
var DropdownRadioGroup = ({
  header,
  value,
  onChange,
  items,
  placeholder = "No options"
}) => {
  const hasItems = items.length > 0;
  const scale = useMotionValue(1);
  const borderWidth = useTransform(scale, [0.95, 1.05], [3, 1.5]);
  return /* @__PURE__ */ jsxs10(Fragment2, { children: [
    header && /* @__PURE__ */ jsx15(DM.Label, { className: dropdownMenuContent({}).label, children: header }),
    hasItems ? /* @__PURE__ */ jsx15(DM.RadioGroup, { value, onValueChange: onChange, asChild: true, children: /* @__PURE__ */ jsx15(motion11.ol, { className: dropdownMenuContent({}).item, children: items.map((item) => /* @__PURE__ */ jsx15(
      DM.RadioItem,
      {
        value: item.value,
        disabled: item.disabled,
        textValue: item.label,
        asChild: true,
        children: /* @__PURE__ */ jsxs10(button_default, { children: [
          /* @__PURE__ */ jsx15(AnimatePresence5, { children: value === item.value && /* @__PURE__ */ jsx15(DM.ItemIndicator, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsxs10("div", { className: dropdownMenuContent({}).indicator, children: [
            /* @__PURE__ */ jsx15(
              motion11.div,
              {
                className: dropdownMenuContent({}).indicatorBorder,
                style: { borderWidth },
                initial: false,
                animate: {
                  borderColor: "#ffff",
                  scale: 1
                },
                exit: {
                  borderColor: "#fff0",
                  scale: 0.95
                },
                transition: {
                  duration: DURATION7.normal,
                  ease: EASING5.out
                }
              }
            ),
            /* @__PURE__ */ jsx15(
              motion11.div,
              {
                className: dropdownMenuContent({}).indicatorDot,
                initial: { scale: 0 },
                animate: {
                  scale: 1,
                  opacity: 1
                },
                exit: {
                  scale: 0,
                  opacity: 0
                },
                transition: SPRING4.bouncy
              }
            )
          ] }) }) }),
          item.label,
          item.shortcut && /* @__PURE__ */ jsx15("span", { style: { marginLeft: "auto", opacity: 0.5, fontSize: "0.875em" }, children: item.shortcut })
        ] })
      },
      item.value
    )) }) }) : /* @__PURE__ */ jsx15(DM.Item, { className: dropdownMenuContent({}).item, disabled: true, children: placeholder })
  ] });
};
var DropdownMenuItem = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx15(DM.Item, { className: dropdownMenuContent({}).item, ...props, children });
};
var DropdownMenuLabel = ({ children }) => {
  return /* @__PURE__ */ jsx15(DM.Label, { className: dropdownMenuContent({}).label, children });
};
var DropdownMenuSeparator = () => {
  return /* @__PURE__ */ jsx15(DM.Separator, { className: dropdownMenuContent({}).separator });
};
var DropdownMenuRadioGroup = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx15(DM.RadioGroup, { ...props, children });
};
var DropdownMenuRadioItem = ({ children, ...props }) => {
  return /* @__PURE__ */ jsxs10(DM.RadioItem, { className: dropdownMenuContent({}).item, ...props, children: [
    /* @__PURE__ */ jsx15(DM.ItemIndicator, { className: dropdownMenuContent({}).indicator, children: /* @__PURE__ */ jsx15("span", { children: "\u2713" }) }),
    children
  ] });
};
var Dropdown = Object.assign(DropdownMenu, {
  RadioGroup: DropdownRadioGroup
});
DropdownMenu.displayName = "DropdownMenu";
DropdownRadioGroup.displayName = "DropdownRadioGroup";
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";
var dropdown_menu_default = Dropdown;

// src/components/dropzone/index.tsx
import { useMemo, useState } from "react";
import { dropZone } from "@pinatagrams/design-system/recipes";
import { AnimatePresence as AnimatePresence6, motion as motion12 } from "motion/react";
import { useDropzone } from "react-dropzone";
import { jsx as jsx16, jsxs as jsxs11 } from "react/jsx-runtime";
var Dropzone = ({
  asChild,
  hint,
  accept,
  maxFiles,
  onInvalidFile,
  onFileDrop,
  size = "element",
  noDragEventsBubbling = false,
  aspectRatio,
  children,
  ...props
}) => {
  void asChild;
  const handleDrop = (files, rejectedFiles) => {
    if (maxFiles && files.length + rejectedFiles.length > maxFiles) {
      onInvalidFile?.(files.length + rejectedFiles.length);
      return;
    }
    if (rejectedFiles.length > 0) {
      onInvalidFile?.(rejectedFiles);
      return;
    }
    onFileDrop?.(files);
  };
  const [pos, setPos] = useState(void 0);
  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject,
    isDragGlobal,
    isDragActive,
    isFocused,
    isFileDialogActive
  } = useDropzone({
    maxFiles,
    accept,
    noDragEventsBubbling,
    multiple: maxFiles ? maxFiles > 1 : true,
    onDrop: (files, fileRejections) => {
      const rejectedFiles = fileRejections.map((rejection) => rejection.file);
      handleDrop(files, rejectedFiles);
    },
    onDragOver: (event) => {
      setPos({ x: event.clientX, y: event.clientY });
    },
    useFsAccessApi: true
  });
  const badgeData = useMemo(() => {
    if (isDragAccept) {
      return { label: "Valid", color: "success" };
    } else if (isDragReject) {
      return { label: "Invalid file type", color: "error" };
    } else {
      return null;
    }
  }, [isDragAccept, isDragReject]);
  const overlayStatus = useMemo(() => {
    if (isDragAccept) {
      return "success";
    } else if (isDragReject) {
      return "error";
    } else if (isFileDialogActive) {
      return "warning";
    } else {
      return "info";
    }
  }, [isDragAccept, isDragReject, isFileDialogActive]);
  const displayOverlay = isDragGlobal || isFocused || isFileDialogActive;
  return /* @__PURE__ */ jsxs11(
    motion12.section,
    {
      ...getRootProps({
        ...props,
        className: dropZone({ size }),
        style: {
          pointerEvents: "auto",
          cursor: isDragActive ? "copy" : "pointer",
          zIndex: displayOverlay ? 1 : -1,
          ...aspectRatio ? { aspectRatio, width: "100%" } : {},
          ...props.style
        }
      }),
      children: [
        /* @__PURE__ */ jsx16("input", { ...getInputProps() }),
        children,
        /* @__PURE__ */ jsx16(AnimatePresence6, { mode: "wait", children: displayOverlay && /* @__PURE__ */ jsx16(
          Overlay,
          {
            status: overlayStatus,
            size: "element",
            elevation: "raised",
            pos,
            exited: !isDragActive,
            children: /* @__PURE__ */ jsxs11(card_default, { variant: "subtle", children: [
              /* @__PURE__ */ jsxs11(text_default, { hierarchy: "subtitle", children: [
                hint,
                badgeData && /* @__PURE__ */ jsx16(badge_default, { color: overlayStatus, children: badgeData.label })
              ] }),
              accept && /* @__PURE__ */ jsx16(Body, { emphasis: "muted", children: accept && `Accepted file types: ${Object.values(accept).flatMap((exts) => exts).join(", ")}` })
            ] })
          },
          "dropzone-overlay"
        ) })
      ]
    }
  );
};
Dropzone.displayName = "Dropzone";
var dropzone_default = Dropzone;

// src/components/select/index.tsx
import React15 from "react";
import { motion as motion13 } from "motion/react";
import { Select as RadixSelect } from "radix-ui";
import { selectRecipe } from "@pinatagrams/design-system/recipes";
import { jsx as jsx17, jsxs as jsxs12 } from "react/jsx-runtime";
var SelectRoot = ({
  value,
  onValueChange,
  open,
  onOpenChange,
  children
}) => {
  const [isOpen, setIsOpen] = React15.useState(open ?? false);
  React15.useEffect(() => {
    if (open !== void 0) {
      setIsOpen(open);
    }
  }, [open]);
  const handleOpenChange = (newOpen) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  return /* @__PURE__ */ jsx17(
    RadixSelect.Root,
    {
      open: isOpen,
      onOpenChange: handleOpenChange,
      value,
      onValueChange,
      children
    }
  );
};
var SelectTrigger = ({ children }) => {
  return /* @__PURE__ */ jsx17(RadixSelect.Trigger, { asChild: true, children });
};
var SelectContent = ({
  children,
  position = "popper",
  sideOffset = 4,
  align = "center",
  ...props
}) => {
  const container = useLayerContainer(2 /* Popover */);
  return /* @__PURE__ */ jsx17(RadixSelect.Portal, { container, children: /* @__PURE__ */ jsx17(
    RadixSelect.Content,
    {
      asChild: true,
      position,
      sideOffset,
      align,
      ...props,
      children: /* @__PURE__ */ jsxs12(
        motion13.div,
        {
          className: selectRecipe({}).content,
          initial: { opacity: 0, scale: 0.95 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.95 },
          transition: { duration: 0.15 },
          children: [
            /* @__PURE__ */ jsx17(RadixSelect.Arrow, { className: selectRecipe({}).arrow }),
            /* @__PURE__ */ jsx17(RadixSelect.Viewport, { className: selectRecipe({}).viewport, children })
          ]
        }
      )
    }
  ) });
};
var SelectItem = ({ children, value, disabled }) => {
  return /* @__PURE__ */ jsx17(RadixSelect.Item, { value, className: selectRecipe().item, asChild: true, disabled, children: /* @__PURE__ */ jsx17(
    motion13.div,
    {
      whileHover: disabled ? void 0 : { outlineWidth: "2px" },
      whileFocus: disabled ? void 0 : { outlineWidth: "2px" },
      transition: {
        duration: 0.1,
        ease: "linear"
      },
      style: {
        willChange: "outline-width"
      },
      children: /* @__PURE__ */ jsx17(RadixSelect.ItemText, { children })
    }
  ) });
};
var Select = Object.assign(SelectRoot, {
  Trigger: SelectTrigger,
  Content: SelectContent,
  Item: SelectItem
});
var select_default = Select;

// src/components/keyboard-hint/index.tsx
import { ArrowFatUpIcon, CommandIcon, OptionIcon } from "@phosphor-icons/react";
import { keyboardHintRecipe } from "@pinatagrams/design-system/recipes";
import { useEffect, useState as useState2 } from "react";
import { jsx as jsx18, jsxs as jsxs13 } from "react/jsx-runtime";
var KeyboardHint = ({ shortcut, modifier }) => {
  const [os, setOs] = useState2("windows");
  const getCtrlIcon = () => {
    if (os === "mac") {
      return /* @__PURE__ */ jsx18(CommandIcon, { color: "currentColor" });
    } else {
      return /* @__PURE__ */ jsx18(Supplemental, { emphasis: "muted", children: "ctrl +" });
    }
  };
  const getModifierIcon = (modifier2) => {
    switch (modifier2) {
      case "ctrl":
        return getCtrlIcon();
      case "shift":
        return /* @__PURE__ */ jsx18(ArrowFatUpIcon, { color: "currentColor" });
      case "alt":
        return /* @__PURE__ */ jsx18(OptionIcon, { color: "currentColor" });
      case "meta":
        return /* @__PURE__ */ jsx18(CommandIcon, { color: "currentColor" });
      default:
        return null;
    }
  };
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("win")) {
      setOs("windows");
    } else if (userAgent.includes("mac")) {
      setOs("mac");
    } else if (userAgent.includes("linux")) {
      setOs("linux");
    }
  }, [shortcut, modifier]);
  return /* @__PURE__ */ jsxs13(Inline, { gap: "sm", align: "center", className: keyboardHintRecipe({}), children: [
    modifier && getModifierIcon(modifier),
    shortcut.toUpperCase()
  ] });
};
KeyboardHint.displayName = "KeyboardHint";
var keyboard_hint_default = KeyboardHint;

// src/components/collection-card/index.tsx
import React16 from "react";
import { collectionCard } from "@pinatagrams/design-system/recipes";
import { jsx as jsx19 } from "react/jsx-runtime";
var CollectionCardRoot = React16.forwardRef(({ ...props }, ref) => {
  const classes = collectionCard();
  return /* @__PURE__ */ jsx19(Card, { ref, as: "article", className: classes.root, ...props });
});
CollectionCardRoot.displayName = "CollectionCardRoot";
var CollectionCardHeader = React16.forwardRef(({ ...props }, ref) => {
  const classes = collectionCard();
  return /* @__PURE__ */ jsx19("header", { ref, className: classes.header, ...props });
});
CollectionCardHeader.displayName = "CollectionCardHeader";
var CollectionCardClientName = React16.forwardRef(({ ...props }, ref) => {
  const classes = collectionCard();
  return /* @__PURE__ */ jsx19("h2", { ref, className: classes.clientName, ...props });
});
CollectionCardClientName.displayName = "CollectionCardClientName";
var CollectionCardContent = React16.forwardRef(({ ...props }, ref) => {
  const classes = collectionCard();
  return /* @__PURE__ */ jsx19("article", { ref, className: classes.content, ...props });
});
CollectionCardContent.displayName = "CollectionCardContent";
var CollectionCardFooter = React16.forwardRef(({ ...props }, ref) => {
  const classes = collectionCard();
  return /* @__PURE__ */ jsx19("footer", { ref, className: classes.footer, ...props });
});
CollectionCardFooter.displayName = "CollectionCardFooter";
var CollectionCardMeta = React16.forwardRef(({ ...props }, ref) => {
  const classes = collectionCard();
  return /* @__PURE__ */ jsx19("div", { ref, className: classes.meta, ...props });
});
CollectionCardMeta.displayName = "CollectionCardMeta";
var CollectionCardListItemComponent = React16.forwardRef(({ ...props }, ref) => {
  const classes = collectionCard();
  return /* @__PURE__ */ jsx19("li", { ref, className: classes.listItem, ...props });
});
CollectionCardListItemComponent.displayName = "CollectionCard.ListItem";
var CollectionCard = Object.assign(CollectionCardRoot, {
  Root: CollectionCardRoot,
  Header: CollectionCardHeader,
  ClientName: CollectionCardClientName,
  Content: CollectionCardContent,
  Footer: CollectionCardFooter,
  Meta: CollectionCardMeta,
  ListItem: CollectionCardListItemComponent
});
CollectionCard.displayName = "CollectionCard";

// src/components/collections-grid/index.tsx
import React17 from "react";
import { collectionsGrid } from "@pinatagrams/design-system/recipes";
import { jsx as jsx20 } from "react/jsx-runtime";
var CollectionsGrid = React17.forwardRef(
  ({ children }, ref) => {
    const { root, list } = collectionsGrid();
    return /* @__PURE__ */ jsx20("section", { ref, "aria-label": "Collections list", className: root, children: /* @__PURE__ */ jsx20("ul", { className: list, children }) });
  }
);
CollectionsGrid.displayName = "CollectionsGrid";

// src/components/timeline/index.tsx
import React18 from "react";
import { timeline } from "@pinatagrams/design-system/recipes";
import { jsx as jsx21, jsxs as jsxs14 } from "react/jsx-runtime";
var CollectionTimeline = React18.forwardRef(
  ({ fulfillAt, fulfillBy, progress, formatDate, formatTimestamp }, ref) => {
    const currentTimestamp = formatTimestamp(/* @__PURE__ */ new Date());
    const { root, labels, track, progress: progressClass, marker } = timeline();
    return /* @__PURE__ */ jsxs14("figure", { className: root, ref, children: [
      /* @__PURE__ */ jsxs14("figcaption", { className: labels, children: [
        /* @__PURE__ */ jsx21("time", { dateTime: fulfillAt?.toString(), children: formatDate(fulfillAt) }),
        /* @__PURE__ */ jsx21("time", { dateTime: fulfillBy?.toString(), children: formatDate(fulfillBy) })
      ] }),
      /* @__PURE__ */ jsxs14(
        "div",
        {
          className: track,
          role: "progressbar",
          "aria-valuenow": Math.round(progress),
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          "aria-label": "Collection fulfillment timeline",
          title: `Current: ${currentTimestamp}`,
          children: [
            /* @__PURE__ */ jsx21(
              "div",
              {
                className: progressClass,
                style: {
                  width: `${progress}%`
                },
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx21(
              "div",
              {
                className: marker,
                style: {
                  left: `${progress}%`
                },
                "aria-hidden": "true",
                title: `Current: ${currentTimestamp}`
              }
            )
          ]
        }
      )
    ] });
  }
);
CollectionTimeline.displayName = "CollectionTimeline";
export {
  ActionHeader,
  AlertDialog,
  badge_default as Badge,
  banner_default as Banner,
  Body,
  button_default as Button,
  card_default as Card,
  CollectionCard,
  CollectionTimeline,
  CollectionsGrid,
  Dialog,
  dropdown_menu_default as DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownRadioGroup,
  dropzone_default as Dropzone,
  Grid,
  Header,
  HeaderActionsSection,
  HeaderRoot,
  HeaderTitleSection,
  Inline,
  keyboard_hint_default as KeyboardHint,
  label_default as Label,
  Main,
  Overlay,
  PageHeader,
  PageSection,
  ScrollContainer,
  select_default as Select,
  Stack,
  Supplemental,
  input_default as TextInput,
  Title,
  toast_default as Toast,
  ToastProvider,
  tooltip_default as Tooltip,
  TooltipProvider
};
//# sourceMappingURL=components.js.map